import { useState } from 'react';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusWarning,
  Table,
  TableColumn,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import {
  ExternalSecretTarget,
  SecretAction,
  SecretState,
  Stage,
  VaultSecretRow,
  WriteResponse,
} from '@internal/backstage-plugin-keyvault-secrets-common';
import { useKeyVaultSecretsClient } from '../api';
import { AddDialog, ConfirmDialog, EditDialog, RemoveDialog } from './dialogs';

const ACTION_LABELS: Record<SecretAction, string> = {
  edit: 'Edit',
  'open-mapping-pr': 'Open mapping PR',
  remove: 'Remove',
  disable: 'Disable',
  enable: 'Enable',
};

function StateLabel({ state }: { state: SecretState }) {
  switch (state) {
    case 'mapped':
      return <StatusOK>mapped</StatusOK>;
    case 'mapped-disabled':
      return <StatusError>mapped, latest version disabled</StatusError>;
    case 'mapped-missing':
      return <StatusError>mapped, missing in the vault</StatusError>;
    case 'orphan':
      return <StatusWarning>not mapped, enabled</StatusWarning>;
    default:
      return <StatusAborted>not mapped, disabled</StatusAborted>;
  }
}

function formatTime(iso: string | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '-';
}

type Open =
  | { kind: 'add' }
  | { kind: SecretAction; row: VaultSecretRow };

export function StageSecretsCard(props: {
  entityRef: string;
  stage: Stage;
  canWrite: boolean;
}) {
  const { entityRef, stage, canWrite } = props;
  const client = useKeyVaultSecretsClient();
  const { value, loading, error, retry } = useAsyncRetry(
    () => client.list(entityRef, stage),
    [client, entityRef, stage],
  );
  const [open, setOpen] = useState<Open | null>(null);
  const [result, setResult] = useState<WriteResponse>();

  const title = `Secrets in ${stage}`;
  if (loading && !value) {
    return (
      <InfoCard title={title}>
        <Progress />
      </InfoCard>
    );
  }
  if (error || !value) {
    return (
      <InfoCard title={title}>
        <ResponseErrorPanel error={error ?? new Error('No response')} />
      </InfoCard>
    );
  }

  const externalSecrets = value.externalSecrets;
  const nameOf = (externalSecretId: string) =>
    externalSecrets.find(target => target.externalSecretId === externalSecretId)
      ?.name ?? externalSecretId;

  // The list reloads after a refused write too: a refusal often means the
  // state changed since it was loaded.
  const write = async (call: () => Promise<WriteResponse>) => {
    try {
      const response = await call();
      setOpen(null);
      setResult(response);
    } finally {
      retry();
    }
  };

  const columns: TableColumn<VaultSecretRow>[] = [
    {
      title: 'Vault secret',
      field: 'vaultName',
      render: row => <code>{row.vaultName}</code>,
    },
    { title: 'State', render: row => <StateLabel state={row.state} /> },
    {
      title: 'Mappings',
      render: row =>
        row.mappings.length === 0 ? (
          '-'
        ) : (
          <>
            {row.mappings.map(mapping => (
              <Typography key={mapping.mappingId} variant="body2">
                {nameOf(mapping.externalSecretId)} / {mapping.secretKey}:{' '}
                {mapping.envNames.length > 0
                  ? `env ${mapping.envNames.join(', ')}`
                  : 'not in env'}
                {mapping.setByTemplate ? ', set by template' : ''}
              </Typography>
            ))}
          </>
        ),
    },
    { title: 'Updated', render: row => formatTime(row.updatedOn) },
    {
      title: 'Pull requests',
      render: row =>
        row.pullRequests.length === 0 ? (
          '-'
        ) : (
          <>
            {row.pullRequests.map(pr => (
              <Typography key={pr.url} variant="body2">
                <Link to={pr.url}>
                  {pr.kind} #{pr.number}
                </Link>
              </Typography>
            ))}
          </>
        ),
    },
  ];
  if (canWrite) {
    columns.push({
      title: 'Actions',
      render: row => (
        <Box display="flex" gridGap={4} flexWrap="wrap">
          {row.actions.map(action => (
            <Button
              key={action}
              size="small"
              variant="outlined"
              color={action === 'remove' || action === 'disable' ? 'secondary' : 'primary'}
              aria-label={`${ACTION_LABELS[action]} ${row.vaultName}`}
              onClick={() => setOpen({ kind: action, row })}
            >
              {ACTION_LABELS[action]}
            </Button>
          ))}
        </Box>
      ),
    });
  }

  return (
    <InfoCard
      title={title}
      subheader={`Vault ${value.vault}`}
      action={
        canWrite && externalSecrets.some(target => target.component) ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen({ kind: 'add' })}
          >
            Add secret
          </Button>
        ) : undefined
      }
    >
      {result && (
        <Box pb={2}>
          <Alert
            severity={result.failedStep ? 'warning' : 'success'}
            onClose={() => setResult(undefined)}
          >
            {result.failedStep
              ? `Partly done, "${result.failedStep}" failed: ${result.message}`
              : result.message}
            {result.pullRequest && (
              <>
                {' '}
                <Link to={result.pullRequest.url}>
                  {result.pullRequest.kind} PR #{result.pullRequest.number}
                </Link>
              </>
            )}
          </Alert>
        </Box>
      )}
      {value.warnings.map(warning => (
        <Box pb={2} key={warning}>
          <Alert severity="warning">{warning}</Alert>
        </Box>
      ))}

      <Table
        options={{ search: false, paging: false, toolbar: false, padding: 'dense' }}
        columns={columns}
        data={value.secrets}
      />

      <Box pt={2}>
        <Typography variant="subtitle2">ExternalSecrets</Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          A change reaches the Secret at the next ESO refresh (refreshInterval 1h).
        </Typography>
        {externalSecrets.map((target: ExternalSecretTarget) => (
          <Typography key={target.externalSecretId} variant="body2">
            {target.namespace}/{target.name}
            {target.component ? ` (${target.component})` : ''}: last refresh{' '}
            {formatTime(target.lastRefresh)}
            {target.ready === false ? ', not ready' : ''}
          </Typography>
        ))}
      </Box>

      {open?.kind === 'add' && (
        <AddDialog
          externalSecrets={externalSecrets}
          onSubmit={request => write(() => client.add(entityRef, stage, request))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'open-mapping-pr' && (
        <AddDialog
          externalSecrets={externalSecrets}
          orphanVaultName={open.row.vaultName}
          onSubmit={request => write(() => client.add(entityRef, stage, request))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'edit' && (
        <EditDialog
          row={open.row}
          onSubmit={secretValue =>
            write(() => client.edit(entityRef, stage, open.row.vaultName, { value: secretValue }))
          }
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'remove' && (
        <RemoveDialog
          row={open.row}
          externalSecrets={externalSecrets}
          onSubmit={mappingIds =>
            write(() => client.remove(entityRef, stage, open.row.vaultName, { mappingIds }))
          }
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'disable' && (
        <ConfirmDialog
          title={`Disable ${open.row.vaultName}`}
          text="Disables every enabled version of this vault secret. The backend refuses while any ExternalSecret or open add PR still uses it."
          submitLabel="Disable"
          onSubmit={() => write(() => client.disable(entityRef, stage, open.row.vaultName))}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'enable' && (
        <ConfirmDialog
          title={`Enable ${open.row.vaultName}`}
          text="Enables the latest version of this vault secret, so the ExternalSecret can sync again."
          submitLabel="Enable"
          onSubmit={() => write(() => client.enable(entityRef, stage, open.row.vaultName))}
          onClose={() => setOpen(null)}
        />
      )}
    </InfoCard>
  );
}
